// BucksSearch.jsx - Without mock data for recipes and pages

import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../stylings/BucksSearch.css';
import { useCart } from '../context/CartContext';
import { useSearch } from '../App';
import axios from 'axios';

function BucksSearch({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const { addToCart } = useCart();
  const searchContext = useSearch();
  
  // API endpoint - same as the one used in BucksShop
  const API_ENDPOINT = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-inventory-product-get';
  
  // Fetch all products when the search modal opens
  useEffect(() => {
    if (isOpen && allProducts.length === 0) {
      fetchAllProducts();
    }
  }, [isOpen, allProducts.length]);
  
  // Fetch all products from the API
  const fetchAllProducts = async () => {
    setIsLoading(true);
    try {
      // Request all products, with a larger page size to get as many as possible in one request
      const response = await axios.get(API_ENDPOINT, {
        params: {
          page: 1,
          page_size: 100, // Adjust as needed depending on your product count
          include_images: true
        }
      });
      
      if (response.data && response.data.products) {
        setAllProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products for search:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Focus on search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Close search on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Debounced search function
  const performSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      if (searchContext) {
        searchContext.setSearchResults([]);
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Store the search query in the context so other components can access it
    if (searchContext) {
      searchContext.setSearchQuery(query);
    }

    // First, search across ALL products regardless of category
    const matchingProducts = allProducts.filter(product => {
      const nameMatch = product.name.toLowerCase().includes(query.toLowerCase());
      const descMatch = product.description && product.description.toLowerCase().includes(query.toLowerCase());
      return nameMatch || descMatch;
    }).map(product => ({
      ...product,
      type: 'product'  // Add 'type' field to match expected format
    }));
    
    // Then filter by selected category for display
    const filteredProducts = activeCategory === 'all' || activeCategory === 'products' 
      ? matchingProducts 
      : matchingProducts.filter(product => 
          product.category && product.category.toLowerCase() === activeCategory
        );

    // Store filtered results in local state for display
    setSearchResults(filteredProducts);
    
    // Store ALL matching products in context (not just filtered ones)
    // This ensures BucksShop.jsx gets all matching products
    if (searchContext) {
      searchContext.setSearchResults(matchingProducts);
    }

    setIsLoading(false);
  }, [activeCategory, searchContext, allProducts]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set a new timeout for the search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300); // 300ms debounce time
  };

  // Handle form submission (for when user presses Enter)
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Perform search immediately
    performSearch(searchQuery);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    
    // Re-trigger search with new category filter
    if (searchQuery.trim()) {
      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Perform search immediately with current query
      performSearch(searchQuery);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleAddToCart = (product) => {
    addToCart({
      ...product,
      quantity: 1
    });
    
    // Show notification
    const notification = document.createElement('div');
    notification.className = 'search-cart-notification';
    notification.textContent = `${product.name} added to basket`;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 2000);
  };

  const navigateTo = (url, params = {}) => {
    // Build URL with query parameters if any
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        queryParams.append(key, value);
      }
      url = `${url}?${queryParams.toString()}`;
    }
    
    window.location.href = url;
    onClose();
  };

  // Calculate total matching products for indicator display
  const getTotalMatchCount = () => {
    if (!searchQuery.trim()) return 0;
    
    return allProducts.filter(p => {
      const nameMatch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const descMatch = p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase());
      return nameMatch || descMatch;
    }).length;
  };

  return (
    <div className={`bucks-search-overlay ${isOpen ? 'open' : ''}`}>
      <div className="search-container">
        <div className="search-header">
          <h2>Search Buck's Farmstand</h2>
          <button 
            className="close-search-btn" 
            onClick={onClose}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSearchSubmit} className="search-form">
          <div className="search-input-container">
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={handleInputChange}
              className="search-input"
              ref={searchInputRef}
              autoComplete="off"
            />
            <button type="submit" className="search-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            {searchQuery && (
              <button 
                type="button" 
                className="clear-input-btn"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  if (searchContext) {
                    searchContext.setSearchQuery('');
                    searchContext.setSearchResults([]);
                  }
                  searchInputRef.current.focus();
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="search-filters">
            <button 
              type="button"
              className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              All
            </button>
            <button 
              type="button"
              className={`filter-btn ${activeCategory === 'products' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('products')}
            >
              Products
            </button>
            {/* Dynamically generate category filter buttons based on available categories */}
            {Array.from(new Set(allProducts.map(p => p.category))).filter(Boolean).map(category => (
              <button
                key={category}
                type="button"
                className={`filter-btn ${activeCategory === category.toLowerCase() ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category.toLowerCase())}
              >
                {category}
              </button>
            ))}
          </div>
        </form>
        
        <div className="search-results-container">
          {isLoading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Searching Buck's harvest...</p>
            </div>
          ) : searchQuery && searchResults.length > 0 ? (
            <div className="search-results">
              <h3 className="results-header">
                Results for "{searchQuery}" 
                {activeCategory !== 'all' && activeCategory !== 'products' && (
                  <span className="filtered-indicator">
                    (Showing {searchResults.length} of {getTotalMatchCount()} results)
                  </span>
                )}
              </h3>
              <div className="results-list">
                {searchResults.map(item => (
                  <div className="search-result-item product-result" key={item.id}>
                    <div className="result-image">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="result-details">
                      <h3>{item.name}</h3>
                      <p className="result-category">{item.category}</p>
                      <p className="result-price">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="result-actions">
                      <button 
                        className="view-product-btn" 
                        onClick={() => navigateTo('/shop', { product: item.id, category: item.category ? item.category.toLowerCase() : 'all', showModal: 'true' })}
                      >
                        View
                      </button>
                      <button 
                        className="add-to-cart-btn" 
                        onClick={() => handleAddToCart(item)}
                        disabled={item.status === 'Out of Stock'}
                      >
                        {item.status === 'Out of Stock' ? 'Out of Stock' : 'Add to Basket'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : searchQuery ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3>No results found</h3>
              <p>We couldn't find anything matching "{searchQuery}"</p>
              <div className="search-suggestions">
                <h4>Popular searches:</h4>
                <div className="suggestion-tags">
                  <button onClick={() => {
                    setSearchQuery('apple');
                    performSearch('apple');
                  }}>Apples</button>
                  <button onClick={() => {
                    setSearchQuery('tomato');
                    performSearch('tomato');
                  }}>Tomatoes</button>
                  <button onClick={() => {
                    setSearchQuery('berry');
                    performSearch('berry');
                  }}>Berries</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="popular-searches">
              <h3>Popular at Buck's</h3>
              <div className="popular-categories">
                <div className="popular-category" onClick={() => navigateTo('/shop')}>
                  <div className="category-image">
                    <img src="https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80" alt="Seasonal Produce" />
                  </div>
                  <h4>Seasonal Produce</h4>
                </div>
                <div className="popular-category" onClick={() => navigateTo('/recipes')}>
                  <div className="category-image">
                    <img src="https://images.unsplash.com/photo-1590165482129-1b8b27698780?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80" alt="Farm Recipes" />
                  </div>
                  <h4>Farm Recipes</h4>
                </div>
                <div className="popular-category" onClick={() => navigateTo('/bucks-box')}>
                  <div className="category-image">
                    <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80" alt="Buck's Box" />
                  </div>
                  <h4>Buck's Box</h4>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional CSS for dynamic search enhancements */}
      <style>
        {`
          .clear-input-btn {
            position: absolute;
            right: 50px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #a67c52;
            font-size: 1.5rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            padding: 0;
            line-height: 1;
          }

          .clear-input-btn:hover {
            background-color: rgba(166, 124, 82, 0.1);
          }

          mark {
            background-color: rgba(166, 124, 82, 0.2);
            color: inherit;
            font-weight: 600;
            padding: 0 2px;
            border-radius: 2px;
          }

          .search-input-container {
            position: relative;
          }

          .search-result-item {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .search-result-item:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }
          
          /* Disabled button styles */
          .add-to-cart-btn:disabled {
            background-color: #cccccc;
            cursor: not-allowed;
          }

          .filtered-indicator {
            font-size: 0.8rem;
            font-weight: normal;
            color: #6b5a44;
            margin-left: 0.5rem;
          }
          
          /* Make sure out-of-season products have visual indication */
          .search-result-item.out-of-season .result-image {
            position: relative;
          }
          
          .search-result-item.out-of-season .result-image::before {
            content: 'Out of Season';
            position: absolute;
            top: 8px;
            left: 8px;
            background-color: #f39c12;
            color: white;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: bold;
            z-index: 2;
          }
        `}
      </style>
    </div>
  );
}

export default BucksSearch;